import { useTranslator } from '@bluebeam/react-i18n';
import {
  MapOverlayMachineEventsEnum,
  useMapOverlayMachine,
} from 'components/Overlays/MapOverlayMachine';
import { useInteractionManager } from 'providers/InteractionManagerProvider';
import { useProjectManager } from 'providers/ProjectManagerProvider';
import { useRFIManager } from 'providers/RFIManagerProvider';
import { useSheetManager } from 'providers/SheetManagerProvider';
import { useUserInfo } from 'providers/UserInfoProvider';
import React, { useMemo } from 'react';
import { RouteComponentProps, useLocation, withRouter } from 'react-router-dom';
import { PostRFI } from 'records/interactions/PostRFI';
import { ViewRFI } from 'records/interactions/ViewRFI';
import { ViewRFIList } from 'records/interactions/ViewRFIList';
import { User } from 'records/User';
import { clearQueryParam } from 'utils/url';
import {
  createViewRfiMachine,
  createViewRfiServices,
  ViewRFIMachineProvider,
} from './ViewRFIMachine';
import ViewRFIOverlayContent from './ViewRFIOverlayContent';

interface ViewRFIOverlayProps extends RouteComponentProps {
  interaction: ViewRFI;
}

const ViewRFIOverlay: React.FC<ViewRFIOverlayProps> = ({
  interaction,
  history,
}) => {
  const { mapOverlayMachineService } = useMapOverlayMachine();
  const location = useLocation();

  const rfiManager = useRFIManager();
  const projectManager = useProjectManager();
  const userInfo = useUserInfo();
  const sheetManager = useSheetManager();
  const interactionManager = useInteractionManager();

  const project = projectManager.getSelectedProject();
  const currentUser = userInfo.getCurrentUser();
  const userEmails = userInfo.getAllUserEmails();

  const { translate } = useTranslator(['toastText']);
  const toastText = {
    rfiDraftSaveError: {
      title: translate('toastText:rfiDraftSaveError.title'),
      message: translate('toastText:rfiDraftSaveError.message'),
    },
    rfiRespondError: {
      title: translate('toastText:rfiRespondError.title'),
      message: translate('toastText:rfiRespondError.message'),
    },
    rfiAttachDiscussionError: {
      title: translate('toastText:rfiAttachDiscussionError.title'),
      message: translate('toastText:rfiAttachDiscussionError.message'),
    },
    rfiReopenError: {
      title: translate('toastText:rfiReopenError.title'),
      message: translate('toastText:rfiReopenError.message'),
    },
    rfiDraftPublishError: {
      title: translate('toastText:rfiDraftPublishError.title'),
      message: translate('toastText:rfiDraftPublishError.message'),
    },
    rfiDraftDeleteError: {
      title: translate('toastText:rfiDraftDeleteError.title'),
      message: translate('toastText:rfiDraftDeleteError.message'),
    },
    rfiForwardReplyError: {
      title: translate('toastText:rfiForwardReplyError.title'),
      message: translate('toastText:rfiForwardReplyError.message'),
    },
    rfiForwardMessageError: {
      title: translate('toastText:rfiForwardMessageError.title'),
      message: translate('toastText:rfiForwardMessageError.message'),
    },
  };

  const appPage = ((pathname: string) => {
    switch (true) {
      case pathname.includes('/sheet'):
        return 'VIEWER';
      case pathname.includes('/map'):
        return 'MAP';
      default:
        return 'PUNCHES';
    }
  })(location.pathname);

  const viewRfiMachine = useMemo(() => {
    return createViewRfiMachine({
      services: createViewRfiServices(
        rfiManager,
        sheetManager,
        userInfo,
        toastText
      ),
      requiredContext: {
        rfi: interaction.rfi,
        project,
        currentUser: currentUser || new User(),
        userEmails,
        appPage,
      },
    });
  }, [interaction.rfi.id]);

  const handleClose = (): void => {
    if (location.pathname.includes('map')) {
      mapOverlayMachineService.send(MapOverlayMachineEventsEnum.CLOSE_OVERLAY);
    } else {
      interactionManager.deleteInteractions('ViewRFI');
    }
    clearQueryParam('rfi', history);
  };

  const onRfiSelection = async (id: string) => {
    const nextLocation = {
      ...location,
      search: `?rfi=${encodeURIComponent(id)}`,
    };

    history.replace(nextLocation);
  };

  const handleBackClick = (): void => {
    const { containerStyles } = interaction;
    const { rfis } = rfiManager;
    const viewRfiListInteraction = new ViewRFIList({
      rfis: rfis.toList(),
      containerStyles,
    });

    if (location.pathname.includes('map')) {
      mapOverlayMachineService.send({
        type: MapOverlayMachineEventsEnum.OPEN_RFI_LIST_OVERLAY,
        data: { interaction: viewRfiListInteraction },
      });
    } else {
      interactionManager.clearInteractions();
      interactionManager.createInteraction(viewRfiListInteraction);
    }
  };

  const onEditPlacement = async (): Promise<void> => {
    const { markup } = interaction;

    const postRFI = new PostRFI({
      rfi: interaction.rfi,
      markup,
      originalMarkup: markup,
      updatingMarkup: true,
      pageid: markup?.fileid,
    });

    if (location.pathname.includes('map')) {
      mapOverlayMachineService.send(MapOverlayMachineEventsEnum.CLOSE_OVERLAY);
      await interactionManager.createInteraction(postRFI);
    } else {
      await interactionManager.updateInteraction(postRFI);
    }
  };

  return (
    <ViewRFIMachineProvider viewRfiMachine={viewRfiMachine}>
      <ViewRFIOverlayContent
        interaction={interaction}
        onClose={handleClose}
        onBackClick={handleBackClick}
        onEditPlacement={onEditPlacement}
        onRfiSelection={onRfiSelection}
      />
    </ViewRFIMachineProvider>
  );
};

export default withRouter(ViewRFIOverlay);
